// src/ProjectsPage.js
//
// UPDATED VERSION: this page now supports full CRUD, not just read.
// New concepts added compared to the original read-only version:
//   - A "New Project" button that opens ProjectForm in create mode
//   - An "Edit" button per row that opens ProjectForm in edit mode,
//     pre-filled with that row's data
//   - A "Delete" button per row that sends a DELETE request
//   - A refreshProjects() function we can call any time we need the
//     table to reflect the latest data (after create/edit/delete)

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import ProjectForm from './ProjectForm';

function ProjectsPage({ token }) {
  const [projects, setProjects] = useState([]);
  // loading gives every page in the app the same "please wait" behavior
  // instead of a blank/empty-looking table for a split second on load.
  const [loading, setLoading] = useState(true);

  // showForm controls whether ProjectForm is currently visible at all.
  // editingProject holds either null (meaning "New Project" mode) or an
  // actual project object (meaning "Edit this specific project" mode) -
  // this is the exact value we pass down as the `project` prop to
  // ProjectForm, which is how ProjectForm knows which mode to be in.
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Pulled the fetch logic out into its own named function (instead of
  // inline inside useEffect) so we can call it AGAIN later, any time
  // data changes - not just once when the page first loads.
  const refreshProjects = () => {
    fetch(`${API_BASE_URL}/api/projects/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setProjects(data);
        }
      })
      .finally(() => setLoading(false));
  };

  // useEffect still runs refreshProjects() once when the page first
  // loads (and again if the token ever changes) - same as before.
  useEffect(() => {
    refreshProjects();
  }, [token]);

  // Called when the user clicks "New Project"
  const handleNewClick = () => {
    setEditingProject(null); // null = create mode
    setShowForm(true);
  };

  // Called when the user clicks "Edit" on a specific row
  const handleEditClick = (project) => {
    setEditingProject(project); // an actual object = edit mode
    setShowForm(true);
  };

  // Called by ProjectForm itself once a save succeeds
  const handleSaved = () => {
    setShowForm(false);
    setEditingProject(null);
    refreshProjects(); // re-fetch so the table shows the new/updated data
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProject(null);
  };

  const handleDelete = async (project) => {
    // window.confirm shows a simple browser popup asking Yes/No - a
    // basic safety check before doing something irreversible like a delete.
    const confirmed = window.confirm(`Delete project "${project.name}"? This cannot be undone.`);
    if (!confirmed) return;

    await fetch(`${API_BASE_URL}/api/projects/${project.id}/`, {
      method: 'DELETE',
      headers: { Authorization: `Token ${token}` },
    });

    // No response body to read for a successful DELETE (DRF returns
    // 204 No Content) - we just refresh the list afterward.
    refreshProjects();
  };

  if (loading) return <p className="text-gray-500">Loading projects...</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Projects</h1>
        <button
          onClick={handleNewClick}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          + New Project
        </button>
      </div>

      {/* Only render the form at all when showForm is true - this is
          the same "conditional rendering" pattern used elsewhere
          (e.g. the error message in Login.js) */}
      {showForm && (
        <ProjectForm
          token={token}
          project={editingProject}
          onSaved={handleSaved}
          onCancel={handleCancel}
        />
      )}

      <table className="w-full bg-white rounded shadow">
        <thead>
          <tr className="text-left border-b">
            <th className="p-3">Name</th>
            <th className="p-3">Status</th>
            <th className="p-3">Start Date</th>
            <th className="p-3">End Date</th>
            <th className="p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={project.id} className="border-b hover:bg-gray-50">
              <td className="p-3">{project.name}</td>
              <td className="p-3">
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">
                  {project.status}
                </span>
              </td>
              <td className="p-3">{project.start_date}</td>
              <td className="p-3">{project.end_date || 'N/A'}</td>
              <td className="p-3">
                <button
                  onClick={() => handleEditClick(project)}
                  className="text-blue-600 text-sm hover:underline mr-3"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(project)}
                  className="text-red-600 text-sm hover:underline"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ProjectsPage;
